import { Schema } from 'effect';
import { HttpApiEndpoint, HttpApiGroup } from 'effect/unstable/httpapi';
import { ApiBadRequest, ApiForbidden, ApiUnauthorized } from './common.js';

/**
 * [A.6.5b] `/v1/billing` contract — single source of truth for the billing slice.
 *
 * Same shape as the A.6.5a jobs slice: this effect `HttpApi` group is the one
 * definition both sides build against. The server
 * (`apps/api/server/src/http/routes/billing-routes.ts` + `getBillingUsage` /
 * `createCheckoutSession` in `billing.ts`) produces these payloads, and the web
 * dashboard consumes `makeBillingClient` from `./billing-client.js` instead of
 * hand-declaring URLs and response interfaces in `apps/web/src/api/types.ts`.
 *
 * Wire-honesty note (mirrors jobs.ts): `credit`, `spend`, and `transactions` are
 * optional here even though the current server always emits them. An API
 * deployed ahead of / behind the dashboard must not hard-fail the billing page
 * on a missing aggregate — the client facade normalizes into the dashboard's
 * view instead. `balance.cents` / `balance.dollars` stay nullable for the same
 * reason: a user with no `credit_balances` row reads as "unknown", not zero.
 */

export const BillingBalance = Schema.Struct({
  cents: Schema.NullOr(Schema.Number),
  dollars: Schema.NullOr(Schema.Number),
  updatedAt: Schema.NullOr(Schema.String),
});
export type BillingBalance = Schema.Schema.Type<typeof BillingBalance>;

export const BillingCredit = Schema.Struct({
  lifetimePurchasedCents: Schema.Number,
  lifetimeGrantedCents: Schema.Number,
  lifetimeSpentCents: Schema.Number,
  /** [A.6.8] True while the account can still claim the $5 signup credit by verifying a card. */
  signupCreditAvailable: Schema.optionalKey(Schema.Boolean),
});
export type BillingCredit = Schema.Schema.Type<typeof BillingCredit>;

/**
 * Keyed by `usage_records.product_type`. The server seeds
 * `ocr/chat/extract/topup/grant` but inserts any product type it sees, so this
 * is an open record rather than a closed struct.
 */
export const BillingSpend = Schema.Record(Schema.String, Schema.Number);
export type BillingSpend = Schema.Schema.Type<typeof BillingSpend>;

export const BillingTransaction = Schema.Struct({
  id: Schema.String,
  productType: Schema.String,
  description: Schema.NullOr(Schema.String),
  totalCostCents: Schema.Number,
  status: Schema.String,
  createdAt: Schema.String,
});
export type BillingTransaction = Schema.Schema.Type<typeof BillingTransaction>;

export const BillingUsageByEndpoint = Schema.Struct({
  endpoint: Schema.String,
  costUsd: Schema.Number,
  requestCount: Schema.Number,
});
export type BillingUsageByEndpoint = Schema.Schema.Type<typeof BillingUsageByEndpoint>;

export const BillingUsageRecent = Schema.Struct({
  endpoint: Schema.String,
  documentId: Schema.NullOr(Schema.String),
  costUsd: Schema.Number,
  vendor: Schema.NullOr(Schema.String),
  model: Schema.NullOr(Schema.String),
  settlementStatus: Schema.optionalKey(Schema.NullOr(Schema.String)),
  deducted: Schema.optionalKey(Schema.NullOr(Schema.Boolean)),
  createdAt: Schema.String,
});
export type BillingUsageRecent = Schema.Schema.Type<typeof BillingUsageRecent>;

/** Edge (D1) usage rollup — `readEdgeBillingSummary` returns zeros, not null. */
export const BillingEdgeUsage = Schema.Struct({
  totalCostUsd: Schema.Number,
  requestCount: Schema.Number,
  byEndpoint: Schema.Array(BillingUsageByEndpoint),
  recent: Schema.Array(BillingUsageRecent),
});
export type BillingEdgeUsage = Schema.Schema.Type<typeof BillingEdgeUsage>;

export const BillingUsage = Schema.Struct({
  balance: BillingBalance,
  credit: Schema.optionalKey(BillingCredit),
  spend: Schema.optionalKey(BillingSpend),
  transactions: Schema.optionalKey(Schema.Array(BillingTransaction)),
  usage: BillingEdgeUsage,
});
export type BillingUsage = Schema.Schema.Type<typeof BillingUsage>;

/** `POST /v1/billing/checkout-sessions` — Stripe hosted-checkout handoff. */
export const CreateCheckoutSessionRequest = Schema.Struct({
  amountCents: Schema.Number,
});
export type CreateCheckoutSessionRequest = Schema.Schema.Type<typeof CreateCheckoutSessionRequest>;

export const CheckoutSession = Schema.Struct({
  url: Schema.String,
});
export type CheckoutSession = Schema.Schema.Type<typeof CheckoutSession>;

/**
 * `GET /v1/billing/topup-session` — settle a returning Stripe checkout.
 * `applied` is false when the session is not yet `paid`, in which case the
 * credit fields are zero rather than absent.
 */
export const TopupSession = Schema.Struct({
  sessionId: Schema.String,
  paymentStatus: Schema.NullOr(Schema.String),
  amountTotalCents: Schema.Number,
  bonusCents: Schema.Number,
  totalCreditedCents: Schema.Number,
  newBalanceCents: Schema.Number,
  applied: Schema.Boolean,
});
export type TopupSession = Schema.Schema.Type<typeof TopupSession>;

const TopupSessionQuery = Schema.Struct({
  session_id: Schema.String,
});

/**
 * [A.6.8] `GET /v1/billing/card-setup-session` — settle a returning card-setup
 * checkout (Stripe setup mode, no charge). `status` says whether the signup
 * credit was granted, was already claimed by this account, or this card
 * already claimed it on another account.
 */
export const CardSetupSession = Schema.Struct({
  sessionId: Schema.String,
  status: Schema.Literals(['granted', 'already_claimed', 'card_already_used', 'incomplete']),
  grantedCents: Schema.Number,
  newBalanceCents: Schema.Number,
});
export type CardSetupSession = Schema.Schema.Type<typeof CardSetupSession>;

export const BillingApiGroup = HttpApiGroup.make('billing')
  .add(
    HttpApiEndpoint.get('getBillingUsage', '/v1/billing/usage', {
      success: BillingUsage,
      error: [ApiUnauthorized],
    }),
  )
  .add(
    HttpApiEndpoint.post('createCheckoutSession', '/v1/billing/checkout-sessions', {
      payload: CreateCheckoutSessionRequest,
      success: CheckoutSession,
      error: [ApiBadRequest, ApiUnauthorized],
    }),
  )
  .add(
    HttpApiEndpoint.get('getTopupSession', '/v1/billing/topup-session', {
      query: TopupSessionQuery,
      success: TopupSession,
      error: [ApiBadRequest, ApiUnauthorized, ApiForbidden],
    }),
  )
  .add(
    HttpApiEndpoint.post('createCardSetupSession', '/v1/billing/card-setup-sessions', {
      success: CheckoutSession,
      error: [ApiUnauthorized],
    }),
  )
  .add(
    HttpApiEndpoint.get('getCardSetupSession', '/v1/billing/card-setup-session', {
      query: TopupSessionQuery,
      success: CardSetupSession,
      error: [ApiBadRequest, ApiUnauthorized, ApiForbidden],
    }),
  );

/**
 * Synchronous decoders for conformance tests and server-side validation, so
 * `apps/api/server` can assert real builder output against the contract without
 * taking a direct dependency on `effect`.
 */
export const decodeBillingUsage = Schema.decodeSync(BillingUsage);
export const decodeCheckoutSession = Schema.decodeSync(CheckoutSession);
export const decodeTopupSession = Schema.decodeSync(TopupSession);
export const decodeCardSetupSession = Schema.decodeSync(CardSetupSession);
